import type { CSSProperties } from 'react';

// biome-ignore lint/suspicious/noExplicitAny: items are polymorphic across call sites (tenants, playbooks, filters, ...)
type AnyData = any;

export interface LabeledSelectProps {
  value: AnyData;
  setValue: (val: AnyData) => void;
  title: string;
  multiple?: boolean;
  items?: Array<AnyData>;
  keyField?: string;
  valueField?: string;
  width?: number | string | null;
  visible?: boolean;
  hideHint?: boolean;
  styleProps?: CSSProperties;
  sx?: CSSProperties;
  disabled?: boolean;
  className?: string;
}

const SELECT: CSSProperties = {
  height: '40px',
  padding: '0 12px',
  fontSize: '14px',
  borderRadius: '4px',
  border: '1px solid var(--chakra-colors-border)',
  background: 'var(--chakra-colors-bg)',
  color: 'var(--chakra-colors-fg)',
  cursor: 'pointer',
};

export const LabeledSelect = ({
  value,
  setValue,
  title,
  multiple = false,
  items = [],
  keyField = '_id',
  valueField = 'name',
  width = null,
  visible = true,
  hideHint = false,
  styleProps = {},
  sx = {},
  disabled = false,
  className,
}: LabeledSelectProps) => {
  if (!visible) {
    return null;
  }

  const style: CSSProperties = {
    ...SELECT,
    ...(width !== null ? { width } : {}),
    ...styleProps,
    ...sx,
  };

  const handleChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    if (multiple) {
      const picked = Array.from(evt.target.selectedOptions).map((o) => o.value);
      setValue(picked);
    } else {
      setValue(evt.target.value);
    }
  };

  const placeholder = title ? `Select: ${title}` : 'Select';
  const showPrefix = !multiple && !hideHint && !!value && !!title;

  return (
    <select
      value={multiple ? (Array.isArray(value) ? value : []) : (value ?? '')}
      onChange={handleChange}
      multiple={multiple}
      disabled={disabled}
      aria-label={`Select ${title}`}
      className={className}
      style={style}
    >
      {!multiple && <option value="">{placeholder}</option>}
      {items.map((item) => (
        <option key={item[keyField]} value={item[keyField]}>
          {showPrefix && item[keyField] === value
            ? `${title}: ${item[valueField]}`
            : item[valueField]}
        </option>
      ))}
    </select>
  );
};
